import React, { useEffect, useState } from "react";
import { Network } from "@capacitor/network";

/**
 * Pre-flight connectivity sentry.
 *
 * On native builds we rely on `@capacitor/network` — which reads the OS
 * radio state directly and correctly reports "airplane mode / no
 * signal" cases the browser cannot see. On web it silently falls back
 * to `navigator.onLine`.
 *
 * Renders an "offline" placeholder in place of `children` while the
 * device is disconnected. Never unmounts children unnecessarily — the
 * consumer app doesn't need to worry about race conditions.
 */
export default function NetworkSentry({ children }) {
  const [isOnline, setIsOnline] = useState(true);

  useEffect(() => {
    let handle;
    (async () => {
      try {
        const s = await Network.getStatus();
        setIsOnline(!!s?.connected);
      } catch {
        setIsOnline(typeof navigator !== "undefined" ? navigator.onLine : true);
      }
      handle = await Network.addListener("networkStatusChange", (status) => {
        setIsOnline(!!status?.connected);
      });
    })();

    // Browser fallback listener — fires even when @capacitor/network is
    // stubbed out under `Capacitor.getPlatform() === "web"`.
    const onlineHandler = () => setIsOnline(true);
    const offlineHandler = () => setIsOnline(false);
    if (typeof window !== "undefined") {
      window.addEventListener("online", onlineHandler);
      window.addEventListener("offline", offlineHandler);
    }
    return () => {
      try { handle?.remove?.(); } catch {}
      if (typeof window !== "undefined") {
        window.removeEventListener("online", onlineHandler);
        window.removeEventListener("offline", offlineHandler);
      }
    };
  }, []);

  if (!isOnline) {
    return (
      <div
        data-testid="network-sentry-offline"
        className="fixed inset-0 z-[150] bg-zinc-950 text-zinc-50 grid place-items-center px-6 pt-safe pb-safe"
      >
        <div className="text-center max-w-md">
          <div className="mx-auto size-14 rounded-2xl bg-[#FF3B30]/15 grid place-items-center mb-4">
            <span className="size-3 rounded-full bg-[#FF3B30] animate-pulse" />
          </div>
          <h2 className="font-display font-black text-2xl tracking-tighter">Network Offline</h2>
          <p className="text-sm text-zinc-500 mt-2">
            proximity.life requires a network connection to operate. Reconnect
            to continue tracking.
          </p>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}
